import type { AnyDashboardArtifactSpec, DashboardEditOperation } from "@t3tools/contracts";
import { invertEdits } from "@t3tools/shared/dashboardEdits";
import { upgradeSpecV1toV2 } from "@t3tools/shared/dashboardSpecUpgrade";

import { DashboardArtifactRequestError, postDashboardArtifactEdits } from "../artifactsReactQuery";
import type { DashboardArtifactEditResult } from "../artifactTypes";

/** Why an undo did not save, in words the panel can show as is. */
export class DashboardUndoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DashboardUndoError";
  }
}

/**
 * Undoes the assistant's edits saved as `version`: inverts `ops` against the
 * version they were applied to (`version - 1`) and saves the inverse through
 * the edits endpoint, as a new version made from `version`.
 */
export async function undoDashboardAssistantEdits(input: {
  ref: string;
  version: number;
  ops: ReadonlyArray<DashboardEditOperation>;
  /** Loads a version's spec, usually from the query cache. */
  loadSpec: (version: number) => Promise<AnyDashboardArtifactSpec>;
  post?: typeof postDashboardArtifactEdits;
}): Promise<DashboardArtifactEditResult> {
  const before = upgradeSpecV1toV2(await input.loadSpec(input.version - 1));
  const inverse = invertEdits(before, input.ops);
  if ("issues" in inverse || inverse.ops.length === 0) {
    throw new DashboardUndoError(
      "This change can't be undone here. Use the version history to go back.",
    );
  }
  try {
    return await (input.post ?? postDashboardArtifactEdits)(input.ref, {
      baseVersion: input.version,
      ops: inverse.ops,
    });
  } catch (error) {
    if (error instanceof DashboardArtifactRequestError && error.status === 409) {
      throw new DashboardUndoError(
        "The dashboard changed after this edit, so it can't be undone here. Use the version history to go back.",
      );
    }
    throw error;
  }
}
